/* global mainScript */
import React, { useState, useContext, useRef, useEffect } from "react";
import styled from "@emotion/styled";
import PropTypes from "prop-types";

//style rules
import { Colors, Typography } from "../../styles";
import Context from "./Context";

const DropDown = ({ DropDownLabel, dropDownFilters, filterKey }) => {
	const [openFiltersDropDown, setOpenFiltersDropDown] = useState(false);

	const dropDownNode = useRef();

	const handleOutsideClick = (e) => {
		if (dropDownNode.current && !dropDownNode.current.contains(e.target)) {
			toggleDropDown();
		}
	};

	useEffect(() => {
		if (openFiltersDropDown) {
			document.addEventListener("mousedown", handleOutsideClick);
		}
		return () => {
			document.removeEventListener("mousedown", handleOutsideClick);
		};
	}, [openFiltersDropDown]);

	const {
		data: { selectedFilters },
		actions: { handleFilterSelect },
	} = useContext(Context);

	const toggleDropDown = () => {
		setOpenFiltersDropDown((currentValue) => !currentValue);
	};

	// Helper function that compares the input filter against the selectedFilters array
	const isChecked = (filter) => {
		if (selectedFilters.length === 0) return;

		return selectedFilters.some((taxonomyObj) =>
			taxonomyObj.filters.some((f) => f.slug === filter.slug)
		);
	};

	const renderFilters = () => {
		// we only run the function when the dropdown is triggered
		if (!openFiltersDropDown) return;

		return (
			<DropDownList>
				{dropDownFilters &&
					dropDownFilters.length > 0 &&
					dropDownFilters.map((filter) => {
						return (
							<DropDownListItem
								key={filter.slug}
								onClick={() =>
									handleFilterSelect({ taxonomy: filterKey, filters: [filter] })
								}
							>
								<CheckboxLabel
									dangerouslySetInnerHTML={{ __html: filter.title }}
								/>
								<input 
									type={"checkbox"} 
									readOnly 
									checked={isChecked(filter)} 
								/>
							</DropDownListItem>
						);
					})}
			</DropDownList>
		);
	};

	return (
		<DropDownContainer ref={dropDownNode}>
			<DropDownTrigger onClick={toggleDropDown} onKeyPress={toggleDropDown}>
				{DropDownLabel}
			</DropDownTrigger>
			{renderFilters()}
		</DropDownContainer>
	);
};

export default DropDown;

DropDown.propTypes = {
	DropDownLabel: PropTypes.string,
	dropDownFilters: PropTypes.array,
	filterKey: PropTypes.string,
};

const DropDownContainer = styled.div`
	width: 100%;
	position: relative;
	background-color: ${Colors.base.white};
`;

const DropDownTrigger = styled.button`
	width: 100%;
	padding: 1.3rem 1.5rem;
	text-transform: capitalize;
	border: 1px solid ${Colors.base.black};
	color: ${Colors.base.black};
	font-weight: 700;
	font-size: 1.3rem;
	line-height: 1.6rem;
	border-radius: 2px;
	cursor: pointer;
	display: flex;
	justify-content: space-between;
	height: unset;
	background: none;
	transition: all 0.5s;

	&::after {
		content: url(${mainScript.assetPath +
		"images/svg/icons/icon-down-arrow.svg"});
	}
`;

const DropDownList = styled.ul`
	width: 100%;
	height: 194px;
	overflow-y: scroll;
	display: flex;
	flex-direction: column;
	gap: 1.6rem;
	margin: 0;
	padding: 1.6rem 1.5rem 2rem;
	position: absolute;
	top: 40px;
	z-index: 2;
	background-color: ${Colors.base.white};
	border-bottom: 1px solid ${Colors.base.black};
	border-left: 1px solid ${Colors.base.black};
	border-right: 1px solid ${Colors.base.black};
`;

const DropDownListItem = styled.li`
	margin: 0;
	padding: 0;
	list-style: none;
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	cursor: pointer;

	input[type="checkbox"] {
		min-width: 15px;
		height: 15px;
		margin: 0;
		border-radius: 3px;

		&:before {
			content: "✓";
			display: block;
			transform: translateX(-100%);
		}

		&:checked::before {
			transform: translateY(-14%);
		}
	}
`;

const CheckboxLabel = styled.label`
	font-size: 1rem !important;
	line-height: 1.6rem;
	font-weight: ${Typography.fontWeight.regular};
	cursor: pointer;
`;
